import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppUrl } from "@/lib/constants";

const FLEET = [
  { name: "Hyundai Xcent",
    category: "Economy Sedan",
    seats: "Up to 4 Passengers",
    luggage: "2-3 Luggage Bags",
    image: "/fleet/xcent.jpg",
    tag: "Economy Sedan" },
  { name: "Swift Dzire Tour",
    category: "Economy Sedan",
    seats: "Up to 4 Passengers",
    luggage: "2-3 Luggage Bags",
    image: "/fleet/dzire-sedan.jpg",
    tag: "Most Booked" },
  { name: "Toyota Etios",
    category: "Economy Sedan",
    seats: "Up to 4 Passengers",
    luggage: "2-3 Luggage Bags",
    image: "/fleet/etios.jpg",
    tag: "Outstation Favourite" },
  { name: "Toyota Innova Crysta",
    category: "Premium SUV / MPV",
    seats: "Up to 7 Passengers",
    luggage: "Large Luggage Capacity",
    image: "/fleet/innova-crysta.jpg",
    tag: "Premium MPV" },
];

const Fleet = () => {
  const quoteUrl = (car) =>
    buildWhatsAppUrl(`Hi Cabsab.com, I'd like a quote for *${car}*. Please share availability and pricing.`);

  return (
    <section id="fleet" data-testid="fleet-section" className="relative py-20 lg:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#19A8E8]">Our Fleet</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight leading-tight">
              Pick the right cab for your <span className="text-[#19A8E8]">journey</span>.
            </h2>
          </div>
          <p className="text-[#64748B] max-w-md leading-relaxed">
            Real Indian commercial taxis with experienced drivers — Economy Sedans for daily trips and the Toyota Innova Crysta for premium outstation and family travel.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {FLEET.map((car, i) => (
            <motion.article
              key={car.name}
              data-testid={`fleet-card-${car.name.replace(/\s+/g, "-").toLowerCase()}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="group bg-white rounded-2xl overflow-hidden border-2 border-[#19A8E8]/15 hover:border-[#19A8E8] hover:shadow-[0_16px_40px_rgba(25,168,232,0.15)] transition-all hover:-translate-y-1"
            >
              <div className="relative overflow-hidden aspect-[16/10] bg-slate-100">
                <img src={car.image} alt={`${car.name} — Cabsab ${car.category}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <span className="absolute top-3 left-3 bg-[#19A8E8] text-[10px] uppercase tracking-[0.18em] font-bold text-white px-3 py-1.5 rounded-full">
                  {car.tag}
                </span>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#19A8E8] mb-1">
                  {car.category}
                </div>
                <h3 className="font-display font-bold text-xl text-[#0F172A] leading-tight">{car.name}</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#19A8E8] bg-[#19A8E8]/10 rounded-full px-2.5 py-1">
                    <Users className="h-3 w-3" /> {car.seats}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F172A] bg-[#F8FAFC] border border-slate-200 rounded-full px-2.5 py-1">
                    {car.luggage}
                  </span>
                </div>
                <Button asChild variant="outline"
                  data-testid={`fleet-quote-btn-${car.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="mt-5 w-full rounded-full border-2 border-slate-200 hover:border-[#19A8E8] hover:bg-[#19A8E8] hover:text-white text-[#0F172A] font-semibold h-11 group/btn">
                  <a href={quoteUrl(car.name)} target="_blank" rel="noopener noreferrer">
                    Contact for Quote
                    <ArrowUpRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                  </a>
                </Button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Fleet;
